import { Address, AddressValue, ArgSerializer, BigUIntValue, ContractFunction, StringValue, TokenIdentifierValue, Transaction, TransactionPayload, TypedValue, U32Value, U64Value } from '@multiversx/sdk-core/out';
import { sendTransactions } from '@multiversx/sdk-dapp/services';
import { refreshAccount } from '@multiversx/sdk-dapp/utils';
import { LEND_GAS_LIMIT, JEWEL_LEND_V2_SC_ADDRESS, ENTER_FARM_GAS_LIMIT, ASH_TOKEN_ID, VEASH_FEE_ADDRESS } from 'config';
import { convertEsdtToWei, getTokenDecimal, YEAR_IN_SECONDS } from 'z/utils';

export const enterFarm = async (chainID: string, scAddress: string, address: string, farmId: number, tokenIds: string[], depositAmounts: string[], leverage: number, slippage: number, debtTokenId: string) => {
  const args: TypedValue[] = [
    new AddressValue(new Address(scAddress)),
  ];
  args.push(new U32Value(tokenIds.length));
  for (let i = 0; i < tokenIds.length; i++) {
    args.push(new TokenIdentifierValue(tokenIds[i]));
    args.push(new U64Value(0));
    args.push(new BigUIntValue(depositAmounts[i]));
  }
  args.push(new StringValue('enterFarm'));
  args.push(new U32Value(farmId));
  args.push(new TokenIdentifierValue(debtTokenId));
  args.push(new U64Value(leverage));
  args.push(new U64Value(slippage));

  const { argumentsString } = new ArgSerializer().valuesToString(args);
  const data = new TransactionPayload(`MultiESDTNFTTransfer@${argumentsString}`);

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(address),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Farm Request',
      errorMessage: 'Error occured during Farm Request',
      successMessage: 'Farm Request Successful',
    }
  });
};

export const exitFarm = async (chainID: string, scAddress: string, address: string, positionId: number, slippage: number) => {
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('exitFarm'))
    .addArg(new U64Value(positionId))
    .addArg(new U64Value(slippage))
    .build();

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Close Position Request',
      errorMessage: 'Error occured during Close Position Request',
      successMessage: 'Close Position Request Successful',
    }
  });
};

export const manageStakeFarm = async (chainID: string, scAddress: string, address: string, functionName: string, amount: string) => {
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('ESDTTransfer'))
    .addArg(new TokenIdentifierValue(ASH_TOKEN_ID))
    .addArg(new BigUIntValue(convertEsdtToWei(amount, getTokenDecimal(ASH_TOKEN_ID))))
    .addArg(new StringValue(functionName))
    .build();

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Stake Request',
      errorMessage: 'Error occured during Stake Request',
      successMessage: 'Increase Stake Successful',
    }
  });
};

export const increaseLockedTime = async (chainID: string, scAddress: string, address: string) => {
  const unlockTime = Math.floor(Date.now() / 1000 + YEAR_IN_SECONDS * 4); // unlock_time max 4 years
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('govIncreaseUnlockTime'))
    .addArg(new U64Value(unlockTime))
    .build();

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Increase Request',
      errorMessage: 'Error occured during Increase Request',
      successMessage: 'Increase Request Successful',
    }
  });
};

export const govWithdraw = async (chainID: string, scAddress: string, address: string) => {
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('govWithdraw'))
    .addArg(new AddressValue(new Address(VEASH_FEE_ADDRESS)))
    .build();

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Withdraw Request',
      errorMessage: 'Error occured during Withdraw Request',
      successMessage: 'Withdraw Request Successful',
    }
  });
};

export const govFeeClaim = async (chainID: string, scAddress: string, address: string) => {
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('feeClaim'))
    .addArg(new AddressValue(new Address(VEASH_FEE_ADDRESS)))
    .build();

  const tx = new Transaction({
    value: '0',
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: ENTER_FARM_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Claim Request',
      errorMessage: 'Error occured during Claim Request',
      successMessage: 'Claim Request Successful',
    }
  });
};

export const wrapEgld = async (chainID: string, scAddress: string, address: string, amount: string) => {
  const data = TransactionPayload.contractCall()
    .setFunction(new ContractFunction('wrapEgld'))
    .build();

  const tx = new Transaction({
    value: amount,
    sender: new Address(address),
    receiver: new Address(scAddress),
    gasLimit: LEND_GAS_LIMIT,
    chainID: chainID,
    data,
  });

  await refreshAccount();
  await sendTransactions({
    transactions: tx,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Wrap Request',
      errorMessage: 'Error occured during Wrap Request',
      successMessage: 'Wrap Request Successful',
    }
  });
};